import React, { useState, useCallback, useRef, useMemo } from "react";
import {
    Box,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    InputAdornment,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { debounce } from "lodash";
import PersonIcon from "@mui/icons-material/Person";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import BusinessIcon from "@mui/icons-material/Business";
import MessageIcon from "@mui/icons-material/Message";

// Define animation styles
const buttonHoverAnimation = { scale: 1.05 };
const buttonTapAnimation = { scale: 0.98 };

export default function ReportForm({ onSubmit, t }) {
    const theme = useTheme();
    const [isHovered, setIsHovered] = useState(false);
    const [isButtonPressed, setIsButtonPressed] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        contactMethod: "",
        contactValue: "",
        company: "",
        domain: "",
        customDomain: "",
        message: "",
    });
    const [errors, setErrors] = useState({
        name: "",
        contactMethod: "",
        contactValue: "",
        company: "",
        domain: "",
        customDomain: "",
        message: "",
    });

    // Contact method options
    const contactMethodOptions = ["Telegram", "Viber", "Phone Number", "Email"];
    // Domain options
    const domainOptions = [
        "Technology",
        "Healthcare",
        "Finance",
        "Retail",
        "Manufacturing",
        "Education",
        "Other",
    ];

    // Memoized validation function
    const validateField = useCallback((name, value, formData) => {
        switch (name) {
            case "name":
                return value.trim().length === 0 ? t("drawer.report.nameRequired") : "";
            case "contactMethod":
                return value.trim().length === 0 ? t("drawer.report.contactMethodRequired") : "";
            case "contactValue":
                if (!formData.contactMethod) return ""; // Skip validation if no contact method is selected
                if (!value.trim()) return t("drawer.report.contactInformationRequired");
                if (formData.contactMethod === "Email") {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    return !emailRegex.test(value) ? t("drawer.report.invalidEmailFormat") : "";
                }
                if (formData.contactMethod === "Phone Number" || formData.contactMethod === "Viber") {
                    const phoneRegex = /^\(\d{3}\)-\d{3}-\d{4}$/;
                    return !phoneRegex.test(value) ? t("drawer.report.invalidPhoneNumberFormat") : "";
                }
                if (formData.contactMethod === "Telegram") {
                    const telegramRegex = /^@[A-Za-z0-9_]{1,}$/;
                    return !telegramRegex.test(value) ? t("drawer.report.invalidTelegramUsername") : "";
                }
                return "";
            case "company":
                return value.trim().length === 0 ? t("drawer.report.companyRequired") : "";
            case "domain":
                return value.trim().length === 0 ? t("drawer.report.businessDomainRequired") : "";
            case "customDomain":
                return formData.domain === "Other" && value.trim().length === 0
                    ? t("drawer.report.customDomainRequired")
                    : "";
            case "message":
                return "";
            default:
                return "";
        }
    }, [t]);

    // Debounced validation
    const debouncedValidateRef = useRef(
        debounce((name, value, formData, callback) => {
            const error = validateField(name, value, formData);
            callback(name, error);
        }, 600)
    ).current;

    // Update single error
    const updateSingleError = useCallback((name, errorMessage) => {
        setErrors(prev => ({
            ...prev,
            [name]: errorMessage,
        }));
    }, []);

    // Validation run flag
    const validationRunRef = useRef(false);

    // Handle field change
    const handleChange = useCallback(
        (e) => {
            const { name, value } = e.target;
            setFormData(prev => ({
                ...prev,
                [name]: value,
                ...(name === "contactMethod" ? { contactValue: "" } : {}), // Reset contactValue when contactMethod changes
            }));

            if (e.type === "blur" || validationRunRef.current) {
                debouncedValidateRef(name, value, { ...formData, [name]: value }, updateSingleError);
            }
        },
        [formData, updateSingleError, debouncedValidateRef]
    );

    // Handle submit
    const handleSubmit = useCallback(
        (event) => {
            event.preventDefault();
            validationRunRef.current = true;

            const newErrors = {
                name: validateField("name", formData.name, formData),
                contactMethod: validateField("contactMethod", formData.contactMethod, formData),
                contactValue: validateField("contactValue", formData.contactValue, formData),
                company: validateField("company", formData.company, formData),
                domain: validateField("domain", formData.domain, formData),
                customDomain: validateField("customDomain", formData.customDomain, formData),
                message: validateField("message", formData.message, formData),
            };

            setErrors(newErrors);

            if (!Object.values(newErrors).some(error => error)) {
                onSubmit(formData);
                setFormData({
                    name: "",
                    contactMethod: "",
                    contactValue: "",
                    company: "",
                    domain: "",
                    customDomain: "",
                    message: "",
                });
            }
        },
        [formData, validateField, onSubmit]
    );

    // Check form validity
    const isFormValid = useMemo(() => {
        const isCustomDomainValid = formData.domain !== "Other" || formData.customDomain.trim();
        return (
            formData.name.trim() &&
            formData.contactMethod.trim() &&
            formData.contactValue.trim() &&
            formData.company.trim() &&
            formData.domain.trim() &&
            isCustomDomainValid &&
            !errors.name &&
            !errors.contactMethod &&
            !errors.contactValue &&
            !errors.company &&
            !errors.domain &&
            !errors.customDomain
        );
    }, [formData,

        errors]);

    // Memoized button styles
    const submitButtonStyles = useMemo(() => ({
        mt: 4,
        alignSelf: "flex-start",
        px: 4,
        py: 2,
        fontSize: "1rem",
        fontWeight: 600,
        borderRadius: 12,
        textTransform: "none",
        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
        color: "#FFFFFF !important",
        boxShadow: isHovered
            ? `0 8px 24px ${theme.palette.primary.main}40`
            : `0 4px 12px ${theme.palette.primary.main}30`,
        transition: "all 0.3s ease",
        "&:hover": {
            boxShadow: `0 12px 32px ${theme.palette.primary.main}50`,
            transform: "translateY(-2px)",
            background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
            color: "#FFFFFF !important",
        },
    }), [isHovered, theme]);

    // Dynamic placeholder for contactValue based on contactMethod
    const contactValuePlaceholder = useMemo(() => {
        switch (formData.contactMethod) {
            case "Telegram":
                return "@test";
            case "Viber":
            case "Phone Number":
                return "(123)-456-7890";
            case "Email":
                return "asdds@gmail.com";
            default:
                return "";
        }
    }, [formData.contactMethod]);

    // Field configurations
    const fieldConfigs = useMemo(() => {
        const configs = [
            {
                id: "name",
                name: "name",
                label: t("drawer.report.fullName"),
                value: formData.name,
                required: true,
                error: !!errors.name,
                helperText: errors.name,
                icon: <PersonIcon sx={{ color: theme.palette.primary.main }} />,
                animationDelay: 0.1,
            },
            {
                id: "contactMethod",
                name: "contactMethod",
                label: t("drawer.report.preferredContactMethod"),
                value: formData.contactMethod,
                required: true,
                error: !!errors.contactMethod,
                helperText: errors.contactMethod,
                type: "select",
                options: contactMethodOptions,
                icon: <ContactMailIcon sx={{ color: theme.palette.primary.main }} />,
                animationDelay: 0.2,
            },
        ];

        // Add contactValue field only if contactMethod is selected
        if (formData.contactMethod) {
            configs.push({
                id: "contactValue",
                name: "contactValue",
                label: t("drawer.report.contactInformation"),
                value: formData.contactValue,
                required: true,
                error: !!errors.contactValue,
                helperText: errors.contactValue,
                icon: <ContactMailIcon sx={{ color: theme.palette.primary.main }} />,
                animationDelay: 0.3,
                placeholder: contactValuePlaceholder,
            });
        }

        // Add remaining fields with dynamic animation delays
        configs.push(
            {
                id: "company",
                name: "company",
                label: t("drawer.report.company"),
                value: formData.company,
                required: true,
                error: !!errors.company,
                helperText: errors.company,
                icon: <BusinessIcon sx={{ color: theme.palette.primary.main }} />,
                animationDelay: formData.contactMethod ? 0.4 : 0.3,
            },
            {
                id: "domain",
                name: "domain",
                label: t("drawer.report.businessDomain"),
                value: formData.domain,
                required: true,
                error: !!errors.domain,
                helperText: errors.domain,
                type: "select",
                options: domainOptions,
                icon: <BusinessIcon sx={{ color: theme.palette.primary.main }} />,
                animationDelay: formData.contactMethod ? 0.5 : 0.4,
            },
            ...(formData.domain === "Other"
                ? [
                    {
                        id: "customDomain",
                        name: "customDomain",
                        label: t("drawer.report.customDomain"),
                        value: formData.customDomain,
                        required: true,
                        error: !!errors.customDomain,
                        helperText: errors.customDomain,
                        icon: <BusinessIcon sx={{ color: theme.palette.primary.main }} />,
                        animationDelay: formData.contactMethod ? 0.6 : 0.5,
                    },
                ]
                : []),
            {
                id: "message",
                name: "message",
                label: t("drawer.report.additionalInformation"),
                value: formData.message,
                multiline: true,
                rows: 4,
                error: !!errors.message,
                helperText: errors.message,
                icon: <MessageIcon sx={{ color: theme.palette.primary.main }} />,
                animationDelay: formData.domain === "Other" ? (formData.contactMethod ? 0.7 : 0.6) : (formData.contactMethod ? 0.6 : 0.5),
            }
        );

        return configs;
    }, [formData, errors, t, contactValuePlaceholder, theme]);

    // Render text or select field
    const renderField = (config) => (
        <motion.div 
            key={config.id} 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: config.animationDelay }}
        >
            {config.type === "select" ? (
                <FormControl
                    fullWidth
                    variant="outlined"
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                            "& fieldset": {
                                borderColor: theme.palette.grey[300],
                                borderWidth: 1,
                            },
                            "&:hover fieldset": {
                                borderColor: theme.palette.primary.main,
                                borderWidth: 2,
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: theme.palette.primary.main,
                                borderWidth: 2,
                            },
                        },
                        "& .MuiInputLabel-root": {
                            color: theme.palette.text.secondary,
                            "&.Mui-focused": {
                                color: theme.palette.primary.main,
                            },
                            "&.MuiInputLabel-shrink": {
                                color: theme.palette.primary.main,
                            },
                        },
                    }}
                    error={config.error}
                >
                    <InputLabel id={`${config.id}-label`}>{config.label}</InputLabel>
                    <Select
                        labelId={`${config.id}-label`}
                        id={config.id}
                        name={config.name}
                        value={config.value}
                        onChange={handleChange}
                        onBlur={handleChange}
                        label={config.label}
                        startAdornment={
                            config.icon && (
                                <InputAdornment position="start">{config.icon}</InputAdornment>
                            )
                        }
                        sx={{
                            color: theme.palette.text.primary,
                            "& .MuiSelect-icon": {
                                color: theme.palette.text.primary,
                            },
                        }}
                    >
                        {config.options.map(option => (
                            <MenuItem 
                                key={option} 
                                value={option}
                                sx={{
                                    backgroundColor: theme.palette.background.paper,
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.main + '20',
                                        color: theme.palette.text.primary,
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: theme.palette.primary.main + '30',
                                        color: theme.palette.text.primary,
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.main + '40',
                                        },
                                    },
                                }}
                            >
                                {config.name === "contactMethod"
                                    ? (() => {
                                        const keyMap = {
                                            'Telegram': 'telegram',
                                            'Viber': 'viber', 
                                            'Phone Number': 'phoneNumber',
                                            'Email': 'email'
                                        };
                                        return t(`drawer.report.contactMethods.${keyMap[option] || option.toLowerCase().replace(/\s+/g, '')}`);
                                    })()
                                    : t(`drawer.report.domains.${option.toLowerCase()}`)}
                            </MenuItem>
                        ))}
                    </Select>
                    {config.helperText && (
                        <Typography variant="caption" sx={{ mt: 1, color: theme.palette.error.main }}>
                            {config.helperText}
                        </Typography>
                    )}
                </FormControl>
            ) : (
                <TextField
                    fullWidth
                    id={config.id}
                    name={config.name}
                    label={config.label}
                    value={config.value}
                    onChange={handleChange}
                    onBlur={handleChange}
                    required={config.required}
                    variant="outlined"
                    multiline={config.multiline}
                    rows={config.rows}
                    error={config.error}
                    helperText={config.helperText}
                    placeholder={config.placeholder}
                    InputProps={{
                        startAdornment: config.icon && (
                            <InputAdornment position="start">{config.icon}</InputAdornment>
                        ),
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                            "& fieldset": {
                                borderColor: theme.palette.grey[300],
                                borderWidth: 1,
                            },
                            "&:hover fieldset": {
                                borderColor: theme.palette.primary.main,
                                borderWidth: 2,
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: theme.palette.primary.main,
                                borderWidth: 2,
                            },
                            "& input": {
                                color: theme.palette.text.primary,
                                "&::placeholder": {
                                    color: theme.palette.text.secondary,
                                    opacity: 1,
                                },
                            },
                            "& textarea": {
                                color: theme.palette.text.primary,
                                "&::placeholder": {
                                    color: theme.palette.text.secondary,
                                    opacity: 1,
                                },
                            },
                        },
                        "& .MuiInputLabel-root": {
                            color: theme.palette.text.secondary,
                            "&.Mui-focused": {
                                color: theme.palette.primary.main,
                            },
                            "&.MuiInputLabel-shrink": {
                                color: theme.palette.primary.main,
                            },
                        },
                        "& .MuiFormHelperText-root": {
                            color: theme.palette.error.main,
                            "&.Mui-error": {
                                color: theme.palette.error.main,
                            },
                        },
                    }}
                />
            )}
        </motion.div>
    );

    return (
        <Box
            component="form"
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2, // Reduced gap for mobile
                flex: 1,
                maxHeight: { xs: "80vh", sm: "auto" }, // Limit height on mobile
                overflowY: { xs: "auto", sm: "visible" }, // Enable scroll on mobile
                p: { xs: 2, sm: 3 }, // Adjust padding for mobile
            }}
        >
            {fieldConfigs.map(renderField)}
            <Box sx={{ mt: "auto", pt: 2 }}>
                <motion.div
                    whileHover={buttonHoverAnimation}
                    whileTap={buttonTapAnimation}
                    style={{ willChange: "transform", transformStyle: "preserve-3d" }}
                >
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSubmit}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        onMouseDown={() => setIsButtonPressed(true)}
                        onMouseUp={() => setIsButtonPressed(false)}
                        disabled={!isFormValid}
                        sx={{
                            ...submitButtonStyles,
                            width: { xs: "100%", sm: "auto" }, // Full width on mobile
                            fontSize: { xs: "1rem", sm: "1.1rem" }, // Slightly smaller font on mobile
                        }}
                    >
                        {t("drawer.report.submit")}
                    </Button>
                </motion.div>
            </Box>
        </Box>
    );
}